import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import Cropper from "cropperjs"

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ACCEPT_TYPES = [ "image/png", "image/jpeg", "image/gif", "image/webp" ]
const MAX_DIMENSION = 1200 // longest side of cropped image (preserves aspect ratio)

export default class extends Controller {
  static targets = [
    "fileInput",
    "dialog",
    "cropImage",
    "cropContainer",
    "backdrop",
    "logoInput",
    "form",
    "preview",
    "progressContainer",
    "progressBar",
    "progressText",
    "errorMessage",
    "inlineError",
    "saveButton"
  ]

  static values = {
    directUploadUrl: String,
    providerPath: String,
    csrfToken: String,
    // When false, the cropped logo's signed_id is written to the logoInput and a
    // preview is shown, but the form is NOT submitted — the surrounding create/edit
    // form submits name + logo together on its own button. Defaults true so the
    // standalone show-page cropper keeps its submit-on-crop behaviour.
    autoSubmit: { type: Boolean, default: true }
  }

  connect() {
    this.objectUrl = null
    this.cropper = null
    this.currentFile = null

    this.boundFileChange = this.handleFileChange.bind(this)
    this.fileInputTarget.addEventListener("change", this.boundFileChange)
  }

  disconnect() {
    this.fileInputTarget?.removeEventListener("change", this.boundFileChange)
    this.cleanup()
  }

  handleFileChange(event) {
    const file = event.target.files?.[0]
    if (!file) return

    if (!ACCEPT_TYPES.includes(file.type)) {
      this.showInlineError("Please choose a PNG, JPEG, GIF, or WebP image.")
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      this.showInlineError("Image must be less than 5MB.")
      return
    }
    this.hideInlineError()

    this.currentFile = file
    this.cleanup()
    this.objectUrl = URL.createObjectURL(file)
    this.cropImageTarget.src = this.objectUrl
    this.cropImageTarget.onload = () => {
      this.hideError()
      this.dialogTarget.showModal()

      // Initialise cropperjs only AFTER the modal's scale-in animation finishes.
      // A hidden <dialog> has no layout, and during the .modal-content scale-in
      // the container is transformed (scaled down) — measuring it in either state
      // renders the crop box misaligned. animationend is precise; the timeout is a
      // fallback if the animation is absent or interrupted. The guard prevents a
      // double init if both fire.
      const initCropper = () => {
        if (this.cropper) return
        this.cropper = new Cropper(this.cropImageTarget, {
          aspectRatio: NaN,
          viewMode: 1,
          dragMode: "crop",
          autoCropArea: 1, // default to the whole image so nothing is cropped off
          restore: false,
          guides: true,
          center: true,
          highlight: false,
          cropBoxMovable: true,
          cropBoxResizable: true,
          toggleDragModeOnDblclick: false
        })
      }
      this.dialogTarget.querySelector(".modal-content")
        ?.addEventListener("animationend", initCropper, { once: true })
      setTimeout(initCropper, 250)
    }
  }

  cancel(event) {
    if (event) event.preventDefault()
    this.cleanup()
    this.resetInputs()
    this.dialogTarget.close()
  }

  backdropClose(event) {
    if (event.target === this.backdropTarget) {
      this.cancel(event)
    }
  }

  save(event) {
    event.preventDefault()
    if (!this.cropper) return

    const canvas = this.cropper.getCroppedCanvas({
      maxWidth: MAX_DIMENSION,
      maxHeight: MAX_DIMENSION,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: "high"
    })
    if (!canvas) return

    // Show the crop immediately in the inline preview (deferred-submit forms).
    if (this.hasPreviewTarget) {
      this.previewTarget.src = canvas.toDataURL("image/jpeg", 0.9)
    }

    this.saveButtonTarget.disabled = true
    this.hideError()

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          this.showError("Failed to process image.")
          this.saveButtonTarget.disabled = false
          return
        }
        const file = new File([ blob ], "logo.jpg", { type: "image/jpeg" })
        this.uploadAndSubmit(file)
      },
      "image/jpeg",
      0.9
    )
  }

  uploadAndSubmit(file) {
    this.progressContainerTarget.style.display = "flex"
    this.progressBarTarget.style.width = "0%"
    this.progressTextTarget.textContent = "0%"

    const upload = new DirectUpload(file, this.directUploadUrlValue, {
      directUploadWillStoreFileWithXHR: (request) => {
        request.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100)
            this.progressBarTarget.style.width = `${percent}%`
            this.progressTextTarget.textContent = `${percent}%`
          }
        })
      }
    })

    upload.create((error, blob) => {
      this.progressContainerTarget.style.display = "none"
      this.saveButtonTarget.disabled = false

      if (error) {
        this.showError("Upload failed. Please try again.")
        return
      }

      this.logoInputTarget.value = blob.signed_id
      this.cleanup()
      this.resetInputs()
      this.dialogTarget.close()
      // Standalone show-page cropper submits its own PATCH form; the create/edit
      // modal defers — it keeps the signed_id and submits with the name field.
      if (this.autoSubmitValue && this.hasFormTarget) {
        this.formTarget.requestSubmit()
      }
    })
  }

  cleanup() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl)
      this.objectUrl = null
    }
    this.currentFile = null
  }

  resetInputs() {
    this.fileInputTarget.value = ""
  }

  showError(message) {
    this.errorMessageTarget.textContent = message
    this.errorMessageTarget.classList.remove("hidden")
  }

  hideError() {
    this.errorMessageTarget.textContent = ""
    this.errorMessageTarget.classList.add("hidden")
  }

  showInlineError(message) {
    this.inlineErrorTarget.textContent = message
    this.inlineErrorTarget.classList.remove("hidden")
  }

  hideInlineError() {
    this.inlineErrorTarget.textContent = ""
    this.inlineErrorTarget.classList.add("hidden")
  }
}
